import { Toast } from './toast.types';

/**
 * Turning an error toast into something a user can paste to an administrator.
 *
 * The message alone is rarely enough to act on — the first two questions back
 * are always "where were you?" and "when?" — so the report carries the page and
 * the time alongside it.
 */

export interface ToastReportContext {
  /** Where the user was. Defaults to the current location. */
  href?: string;
  /** When the toast appeared, ISO-8601. */
  occurredAt: string;
}

/** The pasteable form of a toast. Pure, so it is tested without a DOM. */
export const buildToastReport = (toast: Toast, context: ToastReportContext): string => {
  const lines = [toast.title];
  if (toast.description) lines.push(toast.description);
  const href = context.href ?? (typeof window === 'undefined' ? '' : window.location.href);
  lines.push('', `Page: ${href}`, `Time: ${context.occurredAt}`);
  return lines.join('\n');
};

/**
 * Writes to the clipboard, reporting whether it worked rather than throwing.
 *
 * `navigator.clipboard` is unavailable outside a secure context, so this falls
 * back to a hidden textarea. A failure here must stay quiet: raising an error
 * toast about failing to copy an error toast helps nobody.
 */
export const copyTextToClipboard = async (text: string): Promise<boolean> => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Fall through to the textarea below.
  }

  // Selecting the scratch field takes focus off whatever the user was on — the
  // copy button itself — so both the node and the focus are put back in a
  // `finally`, including when `select()` or `execCommand` throws.
  const previouslyFocused = document.activeElement as HTMLElement | null;
  const field = document.createElement('textarea');
  try {
    field.value = text;
    field.setAttribute('readonly', '');
    // Kept off-screen rather than hidden: `display: none` is not selectable.
    field.style.position = 'fixed';
    field.style.left = '-9999px';
    document.body.appendChild(field);
    field.select();
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    field.remove();
    previouslyFocused?.focus?.();
  }
};
